import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, ReferenceLine } from 'recharts';
import './OilMonitor.css';
import NetPosition from './NetPosition';
import Distribution from './Distribution';
import { API_BASE_URL } from '../config';
import RefreshButton from './RefreshButton';


const OiMonitor = ({ onBack }) => {
  const [chartData, setChartData] = useState([]);
  const [activeTab, setActiveTab] = useState('main');
  const [error, setError] = useState('');
  const [availableIndices] = useState(['NIFTY', 'BANKNIFTY', 'FINNIFTY']);
  const [activeIndex, setActiveIndex] = useState('NIFTY');
  const [positiveCount, setPositiveCount] = useState(0);
  const [negativeCount, setNegativeCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [isUsingCachedData, setIsUsingCachedData] = useState(false);
  const [isDataStale, setIsDataStale] = useState(false);
  const [selectedTime, setSelectedTime] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [sliderValue, setSliderValue] = useState(0);
  const [isMarketOpen, setIsMarketOpen] = useState(false);
  const [atmStrike, setAtmStrike] = useState(null);
  const latestFetchRef = useRef(0);

  // Market hours: 9:15 AM to 3:30 PM (IST)
  const MARKET_START_HOUR = 9;
  const MARKET_START_MINUTE = 15;
  const MARKET_END_HOUR = 15;
  const MARKET_END_MINUTE = 30;

  // Group CE/PE by strike and compute totals; stable reference
  const mergeOIMemoized = useMemo(() => {
    return (rows = []) => {
      const strikeMap = new Map(); // Using Map for better performance with large datasets
      
      // First, group by strike and type, keeping track of the latest timestamp
      rows.forEach((item) => {
        try {
          const strike = Number(item.strike_price);
          if (isNaN(strike)) {
            console.warn('Invalid strike price:', item.strike_price);
            return;
          }
          
          const typ = String(item.option_type || '').trim().toUpperCase();
          if (typ !== 'CE' && typ !== 'PE') {
            console.warn(`Skipping invalid option type: ${typ}`);
            return;
          }
          
          // Parse OI value
          let oi = 0;
          if (item.oi != null) {
            oi = typeof item.oi === 'string' 
              ? parseFloat(item.oi.replace(/,/g, '')) || 0 
              : Number(item.oi) || 0;
          }
          
          // Parse timestamp
          const timestamp = new Date(item.timestamp).getTime();
          if (isNaN(timestamp)) {
            console.warn('Invalid timestamp:', item.timestamp);
            return;
          }
          
          const strikeKey = strike.toString();
          const typeKey = `${strikeKey}_${typ}`;
          
          // Store the latest OI for each strike and type
          const existing = strikeMap.get(typeKey);
          if (!existing || timestamp > existing.timestamp) {
            strikeMap.set(typeKey, { strike, type: typ, oi, timestamp });
          }
          
        } catch (error) {
          console.error('Error processing OI row:', error, item);
        }
      });
      
      // Now combine CE/PE for each strike
      const grouped = {};
      strikeMap.forEach(({ strike, type, oi }) => {
        const strikeKey = strike.toString();
        
        if (!grouped[strikeKey]) {
          grouped[strikeKey] = {
            strike,
            CE: 0,
            PE: 0,
            CE_OI: 0,
            PE_OI: 0,
            total_OI: 0
          };
        }
        
        if (type === 'CE') {
          grouped[strikeKey].CE = Math.abs(oi); // Changed to positive for right-side display
          grouped[strikeKey].CE_OI = oi;
        } else if (type === 'PE') {
          grouped[strikeKey].PE = Math.abs(oi);
          grouped[strikeKey].PE_OI = oi;
        }
        
        // Update total OI
        grouped[strikeKey].total_OI = grouped[strikeKey].CE_OI + grouped[strikeKey].PE_OI;
      });
      
      // Convert to array and sort by strike price in DESCENDING order (higher values at top)
      const result = Object.values(grouped).sort((a, b) => b.strike - a.strike);
      
      console.log('Processed OI data:', {
        inputCount: rows.length,
        outputCount: result.length,
        sample: result.slice(0, 5) // Show first 5 strikes for debugging
      });
      
      return result;
    };
  }, [activeIndex]);

  const fetchData = useCallback(async (index, targetTime = null) => {
    if (!index) {
      setError('No index provided to fetchData');
      return;
    }

    setError('');
    setLoading(true);
    const fetchId = Date.now();
    latestFetchRef.current = fetchId;

    try {
      const url = new URL(`${API_BASE_URL}/api/oi`);
      url.searchParams.append('index', index);
      
      // Don't add timestamp parameter - we'll filter the data client-side
      console.log('Fetching OI data for index:', index);
      
      // Add timestamp to prevent caching
      url.searchParams.append('_t', fetchId);
      
      console.log('Final API URL:', url.toString());

      const timeout = 120000; // 60 seconds for initial OI data fetch
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), timeout);

      const fetchOptions = {
        method: 'GET',
        signal: controller.signal,
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        mode: 'cors'
      };

      const response = await fetch(url, fetchOptions);
      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`API Error: ${response.status} ${response.statusText}`);
      }

      const apiResponse = await response.json();
      
      // Check if the response is successful
      if (!apiResponse.success) {
        throw new Error(apiResponse.message || 'Failed to fetch OI data');
      }

      // Check if we have valid data
      if (!apiResponse.data || !Array.isArray(apiResponse.data)) {
        throw new Error('Invalid data format received from server');
      }

      // Process the response data
      let filteredData = apiResponse.data || [];
      
      // Filter data by selected time if targetTime is provided
      if (targetTime && filteredData.length > 0) {
        console.log('Filtering data for target time:', targetTime.toISOString());
        
        // Create a time window around the selected time (± 5 minutes)
        const timeWindow = 5 * 60 * 1000; // 5 minutes in milliseconds
        const targetTimeMs = targetTime.getTime();
        
        filteredData = filteredData.filter(item => {
          if (!item.timestamp) return false;
          
          try {
            const itemTime = new Date(item.timestamp).getTime();
            const timeDiff = Math.abs(itemTime - targetTimeMs);
            
            // Include items within the time window
            const isWithinWindow = timeDiff <= timeWindow;
            
            if (isWithinWindow) {
              console.log('Found data item:', {
                timestamp: item.timestamp,
                timeDiff: Math.round(timeDiff / 60000) + ' minutes',
                strike: item.strike_price,
                type: item.option_type
              });
            }
            
            return isWithinWindow;
          } catch (e) {
            console.warn('Invalid timestamp format:', item.timestamp);
            return false;
          }
        });
        
        console.log(`Filtered ${apiResponse.data.length} items to ${filteredData.length} items within time window`);
      }
      
      const mergedData = mergeOIMemoized(filteredData);

      // Extract ATM strike from the first data item (all items have same atm_strike)
      const atmValue = filteredData.length > 0 && filteredData[0].atm_strike 
        ? Number(filteredData[0].atm_strike) 
        : null;

      const oiCounts = filteredData.reduce((acc, item) => {
        const oi = Number(item.oi || 0);
        if (oi > 0) acc.positive++;
        else if (oi < 0) acc.negative++;
        return acc;
      }, { positive: 0, negative: 0 });

      // Update state with new data
      if (latestFetchRef.current === fetchId) {
        setChartData(mergedData);
        setPositiveCount(oiCounts.positive);
        setNegativeCount(oiCounts.negative);
        
        // Set ATM strike from data
        setAtmStrike(atmValue);
        
        // Update metadata
        if (apiResponse.metadata) {
          setLastUpdated(apiResponse.metadata.last_updated);
          setIsUsingCachedData(apiResponse.metadata.is_cached || false);
          setIsDataStale(apiResponse.metadata.stale || false);
        } else {
          setLastUpdated(new Date().toISOString());
          setIsUsingCachedData(false);
          setIsDataStale(false);
        }
      }
    } catch (error) {
      console.error('Error fetching OI data:', error);
      
      if (latestFetchRef.current === fetchId) {
        if (error.name === 'AbortError') {
          setError('Request timed out. Please try again.');
        } else if (error.name === 'SyntaxError') {
          setError('Invalid response from server. Please try again later.');
        } else {
          setError(error.message || 'Failed to fetch OI data. Please try again later.');
        }
        
        // Set data to empty arrays on error
        setChartData([]);
        setPositiveCount(0);
        setNegativeCount(0);
      }
    } finally {
      if (latestFetchRef.current === fetchId) {
        setLoading(false);
      }
    }
  }, [mergeOIMemoized, setError, setLoading]);

  // Format last updated time
  const formatLastUpdated = (timestamp) => {
    if (!timestamp) return 'Never';
    
    try {
      const date = new Date(timestamp);
      return date.toLocaleString();
    } catch (e) {
      return 'Unknown';
    }
  };

  // TimeGauge helper functions
  // Convert time to minutes from market start (9:15 AM)
  const timeToMinutes = useCallback((hour, minute) => {
    const marketStartMinutes = MARKET_START_HOUR * 60 + MARKET_START_MINUTE;
    const timeMinutes = hour * 60 + minute;
    return timeMinutes - marketStartMinutes;
  }, []);

  // Convert minutes from market start back to time
  const minutesToTime = useCallback((minutes) => {
    const marketStartMinutes = MARKET_START_HOUR * 60 + MARKET_START_MINUTE;
    const totalMinutes = marketStartMinutes + minutes;
    const hour = Math.floor(totalMinutes / 60);
    const minute = totalMinutes % 60;
    return { hour, minute };
  }, []);

  // Check if market is currently open
  const checkMarketStatus = useCallback(() => {
    const now = new Date();
    const currentHour = now.getHours();
    const currentMinute = now.getMinutes();
    
    const currentMinutesFromStart = timeToMinutes(currentHour, currentMinute);
    const marketEndMinutes = timeToMinutes(MARKET_END_HOUR, MARKET_END_MINUTE);
    
    return currentMinutesFromStart >= 0 && currentMinutesFromStart <= marketEndMinutes;
  }, [timeToMinutes]);

  // Get maximum slider value based on market status
  const getMaxSliderValue = useCallback(() => {
    const marketEndMinutes = timeToMinutes(MARKET_END_HOUR, MARKET_END_MINUTE);
    
    if (isMarketOpen) {
      const now = new Date();
      const currentMinutesFromStart = timeToMinutes(now.getHours(), now.getMinutes());
      return Math.min(currentMinutesFromStart, marketEndMinutes);
    }
    
    return marketEndMinutes;
  }, [isMarketOpen, timeToMinutes]);

  // Generate time labels for the gauge
  const generateTimeLabels = useCallback(() => {
    const labels = [];
    const totalMinutes = timeToMinutes(MARKET_END_HOUR, MARKET_END_MINUTE);
    const interval = Math.floor(totalMinutes / 8); // 8 labels across the gauge
    
    for (let i = 0; i <= totalMinutes; i += interval) {
      const { hour, minute } = minutesToTime(i);
      const timeString = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
      const percentage = (i / totalMinutes) * 100;
      labels.push({ time: timeString, percentage, minutes: i });
    }
    
    return labels;
  }, [timeToMinutes, minutesToTime]);

  // Handle time selection from TimeGauge
  const handleTimeChange = useCallback((time) => {
    setSelectedTime(time);
    // Fetch data for the selected time
    console.log('Selected time:', time.toLocaleTimeString(), 'ISO:', time.toISOString());
    fetchData(activeIndex, time);
  }, [activeIndex, fetchData]);

  // Handle slider change
  const handleSliderChange = useCallback((e) => {
    const value = parseInt(e.target.value);
    const maxValue = getMaxSliderValue();
    
    // Ensure value never exceeds maximum allowed (extra safety)
    const clampedValue = Math.min(value, maxValue);
    
    setSliderValue(clampedValue);
    
    // Convert slider value back to time and notify parent
    const { hour, minute } = minutesToTime(clampedValue);
    
    // Create date for today's date with selected time in IST
    const now = new Date();
    const selectedDateTime = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate(),
      hour,
      minute,
      0,
      0
    );
    
    console.log('Slider changed to:', { hour, minute, selectedDateTime: selectedDateTime.toISOString() });
    handleTimeChange(selectedDateTime);
  }, [getMaxSliderValue, minutesToTime, handleTimeChange]);

  // Initial data fetch
  useEffect(() => {
    fetchData(activeIndex);
    
    // Set up refresh interval (5 minutes)
    const refreshInterval = setInterval(() => {
      fetchData(activeIndex);
    }, 5 * 60 * 1000);
    
    return () => clearInterval(refreshInterval);
  }, [activeIndex, fetchData]);

  // Update document title with loading/error state
  useEffect(() => {
    if (loading) {
      document.title = `Loading OI Data - ${activeIndex} | Stocks4U`;
    } else if (error) {
      document.title = `Error - ${activeIndex} | Stocks4U`;
    } else {
      document.title = `OI Monitor - ${activeIndex} | Stocks4U`;
    }
    
    return () => {
      document.title = 'Stocks4U';
    };
  }, [loading, error, activeIndex]);

  // Initial data load
  useEffect(() => {
    let isMounted = true;
    const controller = new AbortController();

    const loadInitialData = async () => {
      try {
        const url = new URL(`${API_BASE_URL}/api/oi`);
        url.searchParams.append('_t', Date.now());
        const res = await fetch(url, { signal: controller.signal });
        if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
        const json = await res.json();
        if (!json.success) throw new Error(json.message || 'Failed to fetch data');

        if (isMounted && Array.isArray(json.data)) {
          const uniqueIndices = [...new Set(json.data.map((item) => item.index).filter(Boolean))];
          const indicesList = uniqueIndices.map((id) => ({
            id,
            name:
              id === 'NIFTY'
                ? 'NIFTY 50'
                : id === 'BANKNIFTY'
                ? 'BANKNIFTY'
                : id === 'FINNIFTY'
                ? 'FINNIFTY'
                : id === 'MIDCAPNIFTY'
                ? 'NIFTY MIDCAP'
                : id === 'SENSEX'
                ? 'SENSEX'
                : id,
          }));

          if (indicesList.length > 0 && !activeIndex) {
            const defaultIndex = indicesList[0].id;
            setActiveIndex(defaultIndex);
            await fetchData(defaultIndex);
          }
        }
      } catch (err) {
        if (err.name !== 'AbortError' && isMounted) {
          setError('Failed to fetch initial data');
        }
      }
    };

    loadInitialData();

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, []);

  // TimeGauge useEffect hooks
  // Update current time every minute
  useEffect(() => {
    const updateTime = () => {
      const now = new Date();
      setCurrentTime(now);
      setIsMarketOpen(checkMarketStatus());
      
      // If market is open, update slider to current time
      if (checkMarketStatus()) {
        const currentMinutesFromStart = timeToMinutes(now.getHours(), now.getMinutes());
        setSliderValue(Math.max(0, currentMinutesFromStart));
      }
    };

    updateTime();
    const interval = setInterval(updateTime, 60000); // Update every minute
    
    return () => clearInterval(interval);
  }, [checkMarketStatus, timeToMinutes]);

  // Initialize slider value based on selected time or current time
  useEffect(() => {
    if (selectedTime) {
      const selectedDate = new Date(selectedTime);
      const selectedMinutes = timeToMinutes(selectedDate.getHours(), selectedDate.getMinutes());
      setSliderValue(Math.max(0, selectedMinutes));
    } else if (isMarketOpen) {
      const currentMinutesFromStart = timeToMinutes(currentTime.getHours(), currentTime.getMinutes());
      setSliderValue(Math.max(0, currentMinutesFromStart));
    }
  }, [selectedTime, isMarketOpen, currentTime, timeToMinutes]);

  const yTicks = useMemo(() => {
    // No filtering - let the data determine the ticks
    return undefined; // Let Recharts auto-generate ticks based on data
  }, []);

  // Calculate domain for right-side only display (both bars positive)
  const xDomain = useMemo(() => {
    const maxVal = chartData.reduce((max, row) => {
      const ce = Math.abs(Number(row?.CE || 0));
      const pe = Math.abs(Number(row?.PE || 0));
      // For side-by-side bars, we need to account for both bars' width
      return Math.max(max, ce, pe);
    }, 0);

    // Add small padding so bars do not touch chart edges
    const padded = maxVal * 1.1 || 1;
    return [0, padded]; // Only positive values (right side)
  }, [chartData]);

  if (loading) {
    return (
      <div className="oi-monitor-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading data for {activeIndex}...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="oi-monitor-page">
        <div className="error-container">
          <h2>Error Loading Data</h2>
          <p>{error}</p>
          <p className="error-hint">Please ensure the backend server is running and accessible at {API_BASE_URL}</p>
          <button 
            onClick={() => {
              setError('');
              fetchData(activeIndex);
            }} 
            className="retry-button"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="oil-monitor">
      <div className="header-controls">
        <button 
          onClick={onBack} 
          className="back-button"
          style={{
            padding: '8px 16px',
            marginRight: '20px',
            backgroundColor: '#4a90e2',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          ← Back to Dashboard
        </button>
        
        <RefreshButton 
          onRefresh={() => fetchData(activeIndex)} 
          isLoading={loading}
          size="small"
          style={{
            marginRight: '20px'
          }}
        />
        
        <div className="index-buttons" style={{ display: 'flex', gap: '10px' }}>
          {['NIFTY', 'BANKNIFTY', 'FINNIFTY'].map((index) => (
            <button
              key={index}
              onClick={() => setActiveIndex(index)}
              style={{
                padding: '8px 16px',
                backgroundColor: activeIndex === index ? '#4CAF50' : '#f0f0f0',
                color: activeIndex === index ? 'white' : '#333',
                border: '1px solid #ddd',
                borderRadius: '4px',
                cursor: 'pointer',
                fontWeight: activeIndex === index ? 'bold' : 'normal'
              }}
            >
              {index}
            </button>
          ))}
        </div>
      </div>

      {isUsingCachedData && (
        <div className="alert alert-info">
          Showing cached data. Last updated: {formatLastUpdated(lastUpdated)}
        </div>
      )}
      
      {isDataStale && (
        <div className="alert alert-warning">
          Data might be stale. Last updated: {formatLastUpdated(lastUpdated)}
        </div>
      )}

      {/* TimeGauge Component */}
      <div className="time-gauge-container">
        <div className="time-gauge">
          <div className="gauge-header">
            <h3>Time Slider - View Historical OI Data</h3>
            <p className="gauge-description">Drag the slider to view OI data at different times during market hours</p>
          </div>
          <div className="gauge-track">
            <div 
              className="gauge-progress" 
              style={{ 
                width: `${(sliderValue / timeToMinutes(MARKET_END_HOUR, MARKET_END_MINUTE)) * 100}%`
              }}
            />
            <input
              type="range"
              min="0"
              max={getMaxSliderValue()}
              value={sliderValue}
              onChange={handleSliderChange}
              className="gauge-slider"
            />
          </div>
          
          <div className="time-labels">
            {generateTimeLabels().map((label, index) => (
              <div 
                key={index} 
                className="time-label" 
                style={{ 
                  left: `${label.percentage}%`,
                  transform: index === 0 ? 'translateX(0)' : index === generateTimeLabels().length - 1 ? 'translateX(-100%)' : 'translateX(-50%)'
                }}
              >
                {label.time}
              </div>
            ))}
          </div>
        </div>
        
        <div className="gauge-info">
          <div className="market-status">
            <span className="status-indicator">
              {isMarketOpen ? '🟢' : '🔴'}
            </span>
            <span className="status-text">
              Market {isMarketOpen ? 'Open' : 'Closed'}
            </span>
          </div>
          
          <div className="selected-time">
            Selected: {(() => {
              const { hour, minute } = minutesToTime(sliderValue);
              return `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
            })()}
          </div>
          
          {isMarketOpen && (
            <div className="current-time">
              Current: {currentTime.toLocaleTimeString('en-IN', { 
                hour: '2-digit', 
                minute: '2-digit',
                timeZone: 'Asia/Kolkata'
              })}
            </div>
          )}
        </div>
      </div>

      <div className="tabs">
        <button 
          className={activeTab === 'main' ? 'active' : ''}
          onClick={() => setActiveTab('main')}
        >
          OI Chart
        </button>
        <button 
          className={activeTab === 'net' ? 'active' : ''}
          onClick={() => setActiveTab('net')}
        >
          Net Position
        </button>
        <button 
          className={activeTab === 'distribution' ? 'active' : ''}
          onClick={() => setActiveTab('distribution')}
        >
          Distribution
        </button>
      </div>

      <div className="chart-container"
        style={{ width: '100%', minHeight: '600px' }}
          >
            {chartData.length > 0 ? (
              <div className="oi-chart" style={{ width: '100%', height: '100%' }}>
                <div className="chart-footer">
                  <small>
                    Last updated: {formatLastUpdated(lastUpdated)}
                    {isUsingCachedData && ' (Cached)'}
                    {isDataStale && ' (Stale Data)'}
                  </small>
                </div>
                <ResponsiveContainer width="100%" height="90%" minHeight={600} maxHeight={900}>
                  <BarChart
                    data={chartData}
                    layout="vertical"
                    barCategoryGap="20%"
                    barGap={4}
                    margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                  >
                    <XAxis
                      type="number"
                      stroke="#ccc"
                      tickFormatter={(v) => Math.abs(v).toLocaleString()}
                      domain={xDomain}
                      axisLine={{ stroke: '#666', strokeWidth: 1 }}
                      tickLine={{ stroke: '#666' }}
                      allowDataOverflow
                    />
                    <YAxis
                      dataKey="strike"
                      type="category"
                      stroke="#ccc"
                      reversed={false}
                      interval={0}
                      width={80}
                      tick={{ fontSize: 12, fill: '#ccc' }}
                      tickFormatter={(value) => value.toLocaleString()}
                    />
                    <Tooltip
                      formatter={(val, name) => [Math.abs(Number(val)).toLocaleString(), name]}
                      contentStyle={{ backgroundColor: '#1f2937', borderColor: '#374151', color: '#fff' }}
                      labelFormatter={(label) => `Strike: ${label}`}
                    />
                    <Legend />
                    {atmStrike && (
                      <ReferenceLine 
                        y={atmStrike} 
                        stroke="#fbbf24" 
                        strokeWidth={2}
                        strokeDasharray="5 5"
                        label={{ 
                          value: 'ATM', 
                          position: 'insideRight',
                          fill: '#fbbf24',
                          fontWeight: 'bold',
                          fontSize: 12
                        }}
                      />
                    )}
                    <Bar
                      dataKey="CE"
                      fill="#f87171"
                      barSize={12}
                      name="Bears (CE)"
                      stackId="ce"
                    />
                    <Bar
                      dataKey="PE"
                      fill="#4ade80"
                      barSize={12}
                      name="Bulls (PE)"
                      stackId="pe"
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="no-data">
                <p>No OI data available. The market may be closed or there was an issue fetching data.</p>
                <button 
                  onClick={() => fetchData(activeIndex)} 
                  className="retry-button"
                  disabled={loading}
                >
                  {loading ? 'Loading...' : 'Retry'}
                </button>
              </div>
            )}
          </div>

      {activeTab === 'net' && (
        <div className="tab-content">
          <button onClick={() => setActiveTab('main')} className="tab-back">
            ← Back to Chart
          </button>
          <NetPosition />
        </div>
      )}

      {activeTab === 'distribution' && (
        <div className="tab-content">
          <button onClick={() => setActiveTab('main')} className="tab-back">
            ← Back to Chart
          </button>
          <Distribution />
        </div>
      )}
    </div>
  );
};

export default OiMonitor;
